import { Suspense, useCallback, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Link } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { SkeletonBar } from '@givebutter/primitives/skeleton';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useDaysRemainingOnTrial } from 'assets/js/pages/account-settings/account-about/givebutter-plus/useDaysRemainingOnTrial';
import { NucleoIcon } from 'components/nucleo-icon';
import { AccountSwitcher } from 'components/sidebar/AccountSwitcher';
import { CampaignLinks } from 'components/sidebar/CampaignLinks';
import { ChaptersLinks } from 'components/sidebar/ChaptersLinks';
import { ContactLinks } from 'components/sidebar/ContactLinks';
import { DataHygieneLinks } from 'components/sidebar/DataHygieneLinks';
import { EngageLinks } from 'components/sidebar/EngageLinks';
import { HomeLinks } from 'components/sidebar/HomeLinks';
import { PaymentsLinks } from 'components/sidebar/PaymentsLinks';
import { PayoutLinks } from 'components/sidebar/PayoutLinks';
import { PrimaryLinksFallback } from 'components/sidebar/PrimaryLinkFallback';
import { QuickActionsMenu } from 'components/sidebar/QuickActionsMenu';
import { RecentAndPinned } from 'components/sidebar/RecentAndPinned';
import { ReportingLinks } from 'components/sidebar/ReportingLinks';
import { SettingsLinks } from 'components/sidebar/SettingsLinks';
import * as SidebarLink from 'components/sidebar/SidebarLink';
import { TasksLinks } from 'components/sidebar/TasksLinks';
import { WorkflowLinks } from 'components/sidebar/WorkflowLinks';
import { IntercomProductTourTargets } from 'constants/intercom';
import { TestIds } from 'constants/tests';
import { useResponsive } from 'contexts/useResponsive';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { AnimatePresence, motion } from 'framer-motion';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';

const Sidebar = () => {
    const { isMobile, isSidebarCollapsed, setIsMobile, setIsSidebarCollapsed } = useResponsive();

    const handleResize = useCallback(() => {
        if (window.innerWidth < 1024 && !isMobile) {
            setIsMobile(true);
            setIsSidebarCollapsed(true);
        } else if (window.innerWidth >= 1024 && isMobile) {
            setIsMobile(false);
            setIsSidebarCollapsed(false);
        }
    }, [isMobile, setIsMobile, setIsSidebarCollapsed]);

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    return isMobile ? (
        <DialogPrimitive.Root modal={false}>
            <AnimatePresence>
                {isMobile && !isSidebarCollapsed ? (
                    <DialogPrimitive.Portal forceMount={true}>
                        <motion.div
                            initial={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <DialogPrimitive.Overlay className="z-pentOutermost fixed inset-0 bg-black opacity-15" />
                        </motion.div>
                        <DialogPrimitive.Content
                            asChild
                            onInteractOutside={() => setIsSidebarCollapsed(true)}
                            onEscapeKeyDown={() => setIsSidebarCollapsed(true)}
                            onOpenAutoFocus={e => {
                                if (e.target instanceof Element) {
                                    const firstFocusable = e.target.querySelectorAll('a')[0];
                                    if (firstFocusable) {
                                        e.preventDefault();
                                        firstFocusable.focus();
                                    }
                                }
                            }}
                        >
                            <motion.div
                                initial={{ maxWidth: 0 }}
                                animate={{ maxWidth: 304 }}
                                exit={{ maxWidth: 0, transition: { delay: 0.1 } }}
                                transition={{ type: 'spring', duration: 0.35, bounce: 0.3 }}
                                className="z-pentOutermost fixed bottom-0 top-0 w-full overflow-hidden bg-gray-100"
                            >
                                <motion.div
                                    initial={{ opacity: 0, transform: 'translateY(1rem)' }}
                                    animate={{ opacity: 1, transform: 'translateY(0)' }}
                                    exit={{ opacity: 0, transform: 'translateY(1rem)' }}
                                    transition={{ delay: 0.1 }}
                                    className="flex h-full flex-col justify-between overflow-y-auto"
                                >
                                    <SidebarContent />
                                </motion.div>
                            </motion.div>
                        </DialogPrimitive.Content>
                        <DialogPrimitive.Close asChild>
                            <motion.button
                                initial={{ opacity: 0 }}
                                exit={{ opacity: 0, transition: { duration: 0.1 } }}
                                animate={{ opacity: 1, transition: { delay: 0.2 } }}
                                className="z-pentOutermost fixed left-80 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white"
                                aria-label="Close sidebar"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </motion.button>
                        </DialogPrimitive.Close>
                    </DialogPrimitive.Portal>
                ) : null}
            </AnimatePresence>
        </DialogPrimitive.Root>
    ) : (
        <div className="2xl:w-76 fixed bottom-0 top-16 flex w-64 flex-col justify-between overflow-y-auto bg-gray-100">
            <SidebarContent />
        </div>
    );
};

const SidebarContent = () => {
    return (
        <ErrorBoundary fallback={<PrimaryLinksFallback />}>
            <div className="flex h-full flex-col gap-2 px-6 py-4">
                <div className="flex items-center gap-4">
                    <Suspense fallback={<SkeletonBar className="!h-4 !bg-gray-300" />}>
                        <AccountSwitcher />
                    </Suspense>
                    <Suspense
                        fallback={<SkeletonBar className="!size-8 flex-shrink-0 !bg-gray-300" />}
                    >
                        <QuickActionsMenu />
                    </Suspense>
                </div>
                <Suspense fallback={<PrimaryLinksFallback />}>
                    <SidebarLink.Group data-testid={TestIds.Sidebar.LinksContainer}>
                        <HomeLinks />
                        <CampaignLinks />
                        <PaymentsLinks />
                        <ContactLinks />
                        <ChaptersLinks />
                        <EngageLinks />
                        <PayoutLinks />
                        <SettingsLinks />
                    </SidebarLink.Group>

                    <PlusNavSection />

                    <RecentAndPinned />

                    <PlusTrialSection />
                </Suspense>
            </div>
        </ErrorBoundary>
    );
};

const PlusNavSection = () => {
    const { accountId, validGivebutterPlusPlan } = useCurrentAccount();

    return (
        <div data-intercom-target={IntercomProductTourTargets.SidebarPlusContainer}>
            <div className="flex items-center justify-between px-3 py-2 text-xs">
                <div
                    className="font-medium text-gray-700"
                    data-intercom-target={IntercomProductTourTargets.SidebarPlusTitle}
                >
                    Givebutter Plus
                </div>
                {!validGivebutterPlusPlan && (
                    <div>
                        <Link
                            to={`/accounts/${accountId}/settings/about/subscription`}
                            className="flex items-center gap-0.5 text-blue-700"
                        >
                            <NucleoIcon size="sm" icon="circleArrowUp" />
                            Upgrade
                        </Link>
                    </div>
                )}
            </div>
            <SidebarLink.Group>
                <WorkflowLinks />
                <ReportingLinks />
                <DataHygieneLinks />
                <TasksLinks />
            </SidebarLink.Group>
        </div>
    );
};

const PlusTrialSection = () => {
    const { accountId, isCurrentlyOnFreeTrial } = useCurrentAccount();
    const daysRemainingOnTrial = useDaysRemainingOnTrial();

    if (!isCurrentlyOnFreeTrial) {
        return null;
    }

    return (
        <div className="mt-auto flex flex-col items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 p-3 text-xs">
            <div className="flex flex-col items-center gap-1.5">
                <img className={'max-w-28'} src={GivebutterPlusLogo} alt="Givebutter Plus logo" />
                <div className="text-center text-sm">Trial ends in {daysRemainingOnTrial}</div>
            </div>
            <Link to={`/accounts/${accountId}/settings/about/subscription`} className="w-full">
                <Button variant="outline" color="neutral" className="w-full" size="sm">
                    Upgrade
                </Button>
            </Link>
        </div>
    );
};

export { Sidebar };
